// src/pages/HomePage.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import TopNav from '../components/homepage_content/TopNav';
import TopNavBar from '../components/homepage_content/TopNavBar';
import AutoScrollCarousel from '../components/AutoScrollCarousel';
import LiveMatches from '../components/homepage_content/LiveMatches';
import QuickSelection from '../components/homepage_content/QuickSelection';
import BetSlipFloatingButton from '../components/BetSlipFloatingButton';
import Spinner from '../components/PreLoader';
import LanguagePopup from '../components/LanguagePopup';
import { useBetSlip } from '../context/BetSlipContext';
import { HomeImageAPI } from '../services/api';

// Default images as fallback
import welcomeImage from '../assets/welcome.jpeg';
import image1 from '../assets/1.jpeg';
import image2 from '../assets/2.jpeg';
import image3 from '../assets/3.jpeg';

// Image component with fixed dimensions
const FixedImage = ({ src, alt, className = '', onError }) => {
  const [isLoaded, setIsLoaded] = useState(false);
  
  return (
    <div className="relative w-full overflow-hidden bg-gray-800">
      {/* Fixed aspect ratio container - 16:9 for banners, 4:3 for others */}
      <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
        {!isLoaded && (
          <div className="absolute inset-0 flex items-center justify-center bg-gray-800">
            <div className="w-10 h-10 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
          </div>
        )}
        <img
          src={src}
          alt={alt}
          className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-300 ${
            isLoaded ? 'opacity-100' : 'opacity-0'
          } ${className}`}
          loading="lazy"
          onLoad={() => setIsLoaded(true)}
          onError={(e) => {
            if (onError) onError(e);
            setIsLoaded(true);
          }}
        />
      </div>
    </div>
  );
};

const HomePage = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [showLanguagePopup, setShowLanguagePopup] = useState(false);
  const [showAgentModal, setShowAgentModal] = useState(false);
  const [showAgentIcon, setShowAgentIcon] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [homeImages, setHomeImages] = useState({
    welcome_image: { url: welcomeImage, description: 'Welcome to DiceAce12' },
    banner_1: { url: image1, description: 'Special Offers & Promotions' },
    banner_2: { url: image2, description: 'Featured Players' },
    banner_3: { url: image3, description: 'World Cup 2026' },
    agent_modal_image: { url: image3, description: 'FIFA World Cup 2026' }
  });
  const [imagesLoaded, setImagesLoaded] = useState(false);
  const navigate = useNavigate();
  const { betslipCount } = useBetSlip();

  // Fetch home images from backend using HomeImageAPI
  useEffect(() => {
    const fetchHomeImages = async () => {
      try {
        const response = await HomeImageAPI.getHomeImages();
        if (response.success) {
          const imageData = {};
          Object.keys(response.data).forEach(key => {
            const value = response.data[key];
            imageData[key] = {
              url: typeof value === 'string' ? value : value?.url || '',
              description: typeof value === 'object' ? value?.description || '' : ''
            };
          });
          setHomeImages(prev => ({
            ...prev,
            ...imageData
          }));
        }
      } catch (error) {
        console.error('Error fetching home images:', error);
      } finally {
        setImagesLoaded(true);
      }
    };

    fetchHomeImages();
  }, []);

  // Check if user is logged in
  useEffect(() => {
    const checkLoginStatus = () => {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      const user = localStorage.getItem('user') || sessionStorage.getItem('user');
      const isAuthenticated = !!(token || user);
      setIsLoggedIn(isAuthenticated);
      return isAuthenticated;
    };

    const isAuthenticated = checkLoginStatus();
    
    if (!isAuthenticated) {
      const agentModalShown = sessionStorage.getItem('agentModalShown');
      if (!agentModalShown) {
        const timer = setTimeout(() => {
          setShowAgentModal(true);
          sessionStorage.setItem('agentModalShown', 'true');
        }, 2000);
        return () => clearTimeout(timer);
      }
    } else {
      setShowAgentModal(false);
    }
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsLoading(false);
      const preferredLang = localStorage.getItem('preferredLanguage');
      if (!preferredLang) {
        setShowLanguagePopup(true);
      }
    }, 1500);

    return () => clearTimeout(timer);
  }, []);

  const handleBetNowClick = useCallback(() => {
    navigate('/sports');
  }, [navigate]);

  const handleCloseAgentModal = useCallback(() => {
    setShowAgentModal(false);
    setShowAgentIcon(true);
  }, []);

  const handleCloseLanguagePopup = useCallback(() => {
    setShowLanguagePopup(false);
  }, []);

  const handleNavigateToBetSlip = useCallback(() => {
    navigate('/betslip');
  }, [navigate]);

  if (isLoading) {
    return <Spinner />;
  }

  return (
    <div className="min-h-screen bg-deep-black pt-16">
      {/* Language Popup */}
      {showLanguagePopup && (
        <LanguagePopup onClose={handleCloseLanguagePopup} />
      )}

      {/* Agent Modal */}
      {!isLoggedIn && showAgentModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 animate-fade-in">
          <div className="relative max-w-2xl w-full mx-4">
            <button
              onClick={handleCloseAgentModal}
              className="absolute -top-3 -right-3 bg-red-500 text-white rounded-full w-8 h-8 flex items-center justify-center z-50 hover:bg-red-600 transition-colors"
              aria-label="Close agent modal"
            >
              ×
            </button>
            <div className="relative rounded-2xl overflow-hidden border-2 border-blue-500 max-h-[80vh] w-full">
              <div className="relative w-full" style={{ paddingBottom: '75%' }}>
                <img
                  src={homeImages.agent_modal_image?.url || homeImages.banner_3?.url}
                  alt={homeImages.agent_modal_image?.description || 'World Cup 2026'}
                  className="absolute inset-0 w-full h-full object-cover object-center"
                  loading="lazy"
                  onError={(e) => {
                    e.target.src = image3;
                  }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/50 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-4 sm:p-6 text-center">
                  <h3 className="text-xl sm:text-2xl md:text-3xl font-bold text-white mb-1 sm:mb-2">
                    {homeImages.agent_modal_image?.description || 'FIFA World Cup'}
                  </h3>
                  <p className="text-gray-300 text-sm sm:text-base mb-3 sm:mb-4">
                    USA, CANADA, MEXICO 26
                  </p>
                  <button
                    onClick={handleBetNowClick}
                    className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 sm:py-3 px-6 sm:px-8 rounded-lg transition-colors duration-300 text-sm sm:text-base"
                  >
                    Bet Now
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Small Agent Icon */}
      {!isLoggedIn && showAgentIcon && (
        <div
          className="fixed bottom-6 left-6 z-40 cursor-pointer animate-fade-in"
          onClick={() => setShowAgentModal(true)}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => e.key === 'Enter' && setShowAgentModal(true)}
          aria-label="Chat with Agent"
        >
          <div className="relative group">
            <div className="relative w-14 h-14 rounded-full overflow-hidden border-2 border-blue-500 shadow-lg">
              <img
                src={homeImages.agent_modal_image?.url || homeImages.banner_3?.url}
                alt={homeImages.agent_modal_image?.description || 'Agent'}
                className="w-full h-full object-cover"
                loading="lazy"
                onError={(e) => {
                  e.target.src = image3;
                }}
              />
            </div>
            
            <div className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center animate-pulse">
              !
            </div>
            
            <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-1 bg-gray-900 text-white text-sm rounded-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
              {homeImages.agent_modal_image?.description || 'FIFA World Cup 26'}
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <Header />
      <TopNavBar />
      
      <div className="w-full overflow-hidden">
        <AutoScrollCarousel />
      </div>
      
      <TopNav />
      
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {betslipCount > 0 && (
          <BetSlipFloatingButton 
            count={betslipCount}
            onClick={handleNavigateToBetSlip}
          />
        )}

        <div className="mb-8">
          <LiveMatches />
        </div>
      </main>

      {/* Promotional Banner 1 - Desktop: 16:9, Mobile: 4:3 */}
      <div className="mb-8 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
        <div className="relative rounded-xl overflow-hidden card-hover group">
          <div className="relative w-full">
            {/* Desktop: 16:9, Mobile: 4:3 via media queries */}
            <div className="relative w-full" style={{ paddingBottom: '75%' }}>
              <div className="sm:hidden absolute inset-0">
                <img
                  src={homeImages.banner_1?.url || image1}
                  alt={homeImages.banner_1?.description || 'Special Offer'}
                  className="w-full h-full object-cover cursor-pointer transition-transform duration-300 group-hover:scale-[1.02]"
                  loading="lazy"
                  onError={(e) => {
                    e.target.src = image1;
                  }}
                />
              </div>
              <div className="hidden sm:block absolute inset-0" style={{ paddingBottom: '56.25%' }}>
                <img
                  src={homeImages.banner_1?.url || image1}
                  alt={homeImages.banner_1?.description || 'Special Offer'}
                  className="absolute inset-0 w-full h-full object-cover cursor-pointer transition-transform duration-300 group-hover:scale-[1.02]"
                  loading="lazy"
                  onError={(e) => {
                    e.target.src = image1;
                  }}
                />
              </div>
            </div>
            {/* Banner Description Overlay */}
            {homeImages.banner_1?.description && (
              <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/80 to-transparent">
                <p className="text-white font-semibold text-lg md:text-xl">
                  {homeImages.banner_1.description}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-6">
        <div className="mb-8">
          <QuickSelection />
        </div>

        {/* Players Banner - Fixed 16:9 aspect ratio */}
        <div className="mb-8 relative rounded-xl overflow-hidden card-hover group">
          <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
            <img
              src={homeImages.banner_2?.url || image2}
              alt={homeImages.banner_2?.description || 'Featured Players'}
              className="absolute inset-0 w-full h-full object-cover cursor-pointer transition-transform duration-300 group-hover:scale-[1.02]"
              loading="lazy"
              onError={(e) => {
                e.target.src = image2;
              }}
            />
            {/* Banner Description Overlay */}
            {homeImages.banner_2?.description && (
              <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/80 to-transparent">
                <p className="text-white font-semibold text-lg md:text-xl">
                  {homeImages.banner_2.description}
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Welcome Banner - Fixed 16:9 aspect ratio */}
        
      </main>
      
      <Footer />
    </div>
  );
};

export default HomePage;